import React from 'react';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const ExecutiveChart = ({ 
  title, 
  data, 
  type = 'line', 
  height = 300,
  showLegend = true,
  dataKeys = [],
  colors = ['#2563EB', '#10B981', '#F59E0B']
}) => {
  const formatTooltipValue = (value, name) => {
    if (name?.includes('$') || name?.includes('Cost') || name?.includes('Revenue')) {
      return [`$${value?.toLocaleString()}`, name];
    }
    if (name?.includes('%')) {
      return [`${value}%`, name];
    }
    return [value?.toLocaleString(), name];
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center space-x-2 text-sm">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: entry?.color }}
              />
              <span className="text-muted-foreground">{entry?.name}:</span>
              <span className="font-medium text-foreground">
                {formatTooltipValue(entry?.value, entry?.name)?.[0]}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderChart = () => {
    const commonProps = {
      data,
      margin: { top: 5, right: 30, left: 20, bottom: 5 }
    };

    switch (type) {
      case 'area':
        return (
          <AreaChart {...commonProps}>
            <defs>
              {colors?.map((color, index) => (
                <linearGradient key={index} id={`gradient${index}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={color} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={color} stopOpacity={0}/>
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#64748B" />
            <YAxis tick={{ fontSize: 12 }} stroke="#64748B" />
            <Tooltip content={<CustomTooltip />} />
            {showLegend && <Legend />}
            {dataKeys?.map((key, index) => (
              <Area
                key={key}
                type="monotone"
                dataKey={key}
                stroke={colors?.[index % colors?.length]}
                fill={`url(#gradient${index % colors?.length})`}
                strokeWidth={2}
              />
            ))}
          </AreaChart>
        );
      
      case 'bar':
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#64748B" />
            <YAxis tick={{ fontSize: 12 }} stroke="#64748B" />
            <Tooltip content={<CustomTooltip />} />
            {showLegend && <Legend />}
            {dataKeys?.map((key, index) => (
              <Bar
                key={key}
                dataKey={key}
                fill={colors?.[index % colors?.length]}
                radius={[4, 4, 0, 0]}
              />
            ))}
          </BarChart>
        );
      
      default:
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#64748B" />
            <YAxis tick={{ fontSize: 12 }} stroke="#64748B" />
            <Tooltip content={<CustomTooltip />} />
            {showLegend && <Legend />}
            {dataKeys?.map((key, index) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={colors?.[index % colors?.length]}
                strokeWidth={3}
                dot={{ fill: colors?.[index % colors?.length], strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: colors?.[index % colors?.length], strokeWidth: 2 }}
              />
            ))}
          </LineChart>
        );
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 card-shadow">
      <h3 className="text-lg font-semibold text-foreground mb-4">{title}</h3>
      <div style={{ width: '100%', height }}>
        <ResponsiveContainer>
          {renderChart()}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ExecutiveChart;